import { motion } from "framer-motion";
import React from "react";
import { forkJoin, Subscription } from "rxjs";
import ConfigProps from "../../constants/configProps";
import NavigationTokens from "../../constants/navigationTokens";
import { IList } from "../../interfaces/IList";
import ConfigPropService from "../../utilities/configPropService";
import EnvironmentService from "../../utilities/environmentService";
import MyListsService from "../../utilities/myListsService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import ArrowIcon from "../arrowIcon/arrowIcon";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import AddList from "./addList";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { SuccessAlert } from "wbm-generic-components";

import {
  IFavoritesList,
  IListItemAction,
  INewListItemsActionResponse,
} from "@wbmason/my-list-service";
import LoginUrlService from "../../utilities/loginUrlService";
import QueryStringService from "../../utilities/queryStringService";
import {
  variants,
  variantsReverse,
} from "../navMenu/framerMotionSlideVariants";
import List from "./list";
import "./myLists.scss";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";

const ListTypes = [
  {
    key: "Personal",
    underlineClasses: "left",
    listClass: "",
  },
  {
    key: "Account",
    underlineClasses: "right",
    listClass: "account-lists",
  },
];

const successAlertMsg = {
  attentionText: "Success!",
  text: "Your list has been added.",
};

export interface IMyListsProps {
  isLoggedIn: boolean;
  isCookied: boolean;
  closeRight?: boolean;
  className?: string;
  isSecondary?: boolean;
  itemsToAdd: IListItemAction[] | null;
  actionType: number;
  setOpen: (value: boolean) => void;
  addToListEnabled: boolean;
}

export interface IMyListsState {
  selectedListType: string;
  lists: Record<string, IList[]>;
  manageListsHref: string;
  myListDetailsHref: string;
  loginHref: string;
  useDefaultFavoritesList: boolean;
  isEcom: boolean;
  listsLoaded: boolean;
  showSuccessAlert: boolean;
  latestAddedList: INewListItemsActionResponse | null;
}

class MyLists extends React.Component<IMyListsProps, IMyListsState> {
  private navTokenSub: Subscription | undefined;
  private configPropSub: Subscription | undefined;
  private listItemsSub: Subscription | undefined;
  private loginUrlSub: Subscription | undefined;
  private manageGroupLists: boolean = false;
  private manageRestrictions: boolean = false;

  constructor(props: IMyListsProps) {
    super(props);

    const lists: Record<string, IList[]> = {};

    this.state = {
      selectedListType: ListTypes[1].key,
      lists: lists,
      manageListsHref: "",
      myListDetailsHref: "",
      loginHref: "",
      useDefaultFavoritesList: false,
      isEcom: EnvironmentService.get().isEcom === true,
      listsLoaded: false,
      showSuccessAlert: false,
      latestAddedList: null,
    };

    this.selectListType = this.selectListType.bind(this);
    this.selectList = this.selectList.bind(this);
    this.setListState = this.setListState.bind(this);
  }

  isAuthorized() {
    return this.props.isLoggedIn || this.props.isCookied;
  }

  componentWillUnmount() {
    if (this.navTokenSub) {
      this.navTokenSub.unsubscribe();
    }

    if (this.configPropSub) {
      this.configPropSub.unsubscribe();
    }

    if (this.listItemsSub) {
      this.listItemsSub.unsubscribe();
    }

    if (this.loginUrlSub) {
      this.loginUrlSub.unsubscribe();
    }
  }

  componentDidMount() {
    forkJoin([
      ConfigPropService.get([
        ConfigProps.UseDefaultFavoritesList,
        ConfigProps.ManageGroupLists,
        ConfigProps.ManageRestrictions,
        ConfigProps.EnableResponsiveMyLists,
      ]),
      NavigationTokensService.get([
        NavigationTokens.myLists,
        NavigationTokens.myListDetails,
      ]),
    ]).subscribe((responses) => {
      const configProps = responses[0];
      const navTokens = responses[1];

      var enableResponsiveMyLists = ConfigPropService.isYesOrTrue(
        configProps[ConfigProps.EnableResponsiveMyLists],
      );

      const responsiveMyListsLink = WBMUrlService.getMyListsUrl(
        BaseHostUrlService.get(),
        `${
          enableResponsiveMyLists
            ? EnvironmentService.get().ecomVnextUrl
            : EnvironmentService.get().ecom
        }`,
        enableResponsiveMyLists,
      );

      this.manageGroupLists = ConfigPropService.isYesOrTrue(
        configProps[ConfigProps.ManageGroupLists],
      );
      this.manageRestrictions = ConfigPropService.isYesOrTrue(
        configProps[ConfigProps.ManageRestrictions],
      );

      this.setState({
        useDefaultFavoritesList: ConfigPropService.isYesOrTrue(
          configProps[ConfigProps.UseDefaultFavoritesList],
        ),
        manageListsHref: enableResponsiveMyLists
          ? responsiveMyListsLink
          : navTokens[NavigationTokens.myLists],
        myListDetailsHref: navTokens[NavigationTokens.myListDetails],
      });

      if (this.isAuthorized()) {
        this.listItemsSub = MyListsService.getLists(
          this.hideAccountLists(),
        ).subscribe((response) => {
          const hasAccountLists = response.some(
            (list) => list.listType.name === ListTypes[1].key,
          );

          if (!hasAccountLists) {
            this.setState({
              selectedListType: ListTypes[0].key,
            });
          }

          const lists = this.state.lists;
          let selectedListType = this.state.selectedListType;
          const cachedSelectListId = MyListsService.getSelectedListId();

          for (let i = 0; i < response.length; i++) {
            const list = response[i];

            if (!lists[list.listType.name]) {
              lists[list.listType.name] = [];
            }

            if (cachedSelectListId) {
              list.isSelected = list.id === cachedSelectListId;
            }

            if (list.isDefault && this.state.useDefaultFavoritesList) {
              selectedListType = list.listType.name;

              if (!cachedSelectListId) {
                MyListsService.setSelectedListId(list.id);
              }
            }

            lists[list.listType.name].push(list);
          }

          for (let i = 0; i < ListTypes.length; i++) {
            if (!lists[ListTypes[i].key]) {
              lists[ListTypes[i].key] = [];
            }

            lists[ListTypes[i].key].sort((lhs, rhs) => {
              if (lhs.isDefault) {
                return -1;
              }

              if (rhs.isDefault) {
                return 1;
              }

              return 0;
            });
          }

          let listAdjusted = false;

          if (this.props.itemsToAdd && this.props.itemsToAdd.length > 0) {
            const selectedListId = QueryStringService.getParam("listid");
            let adjustedLists = this.state.lists;
            if (selectedListId && Object.entries(adjustedLists).length) {
              for (const listType in adjustedLists) {
                if (
                  adjustedLists[listType].find((o) => o.id === selectedListId)
                ) {
                  adjustedLists[listType] = adjustedLists[listType].sort((x) => {
                    return x.id === selectedListId ? -1 : 0;
                  });
                  this.setState({
                    lists: adjustedLists,
                    selectedListType: listType,
                    listsLoaded: true,
                  });
                  listAdjusted = true;
                  break;
                }
              }
            }
          }
          
          if (!listAdjusted) {
            this.setListState(lists, selectedListType);
          }
        });
      } else {
        this.setState({
          selectedListType: ListTypes[0].key,
        });
      }
    });

    this.loginUrlSub = LoginUrlService.get().subscribe((loginUrl) => {
      this.setState({ loginHref: loginUrl });
    });
  }

  setListState(lists: Record<string, IList[]>, selectedListType: string) {
    this.setState({
      lists: lists,
      selectedListType: selectedListType,
      listsLoaded: true,
    });
  }

  selectListType(value: string) {
    if (this.state.selectedListType !== value) {
      this.setState({
        selectedListType: value,
      });
    }
  }

  selectList(list: IList) {
    const lists = this.state.lists;
    for (const key in lists) {
      for (let i = 0; i < lists[key].length; i++) {
        const current = lists[key][i];

        current.isSelected = false;

        if (this.state.useDefaultFavoritesList) {
          current.isDefault = false;
        }

        if (current.id === list.id) {
          current.isSelected = true;

          if (this.state.useDefaultFavoritesList) {
            current.isDefault = true;
          }

          MyListsService.setSelectedListId(current.id);
        }
      }
    }

    MyListsService.selectList(list).subscribe();

    this.setState({
      lists: lists,
    });
  }

  createList = (response: IFavoritesList) => {
    let lists = { ...this.state.lists };
    if (!lists[response.listType.name]) {
      lists[response.listType.name] = [];
    }
    lists[response.listType.name].push(
      MyListsService.convertResponseToList(response),
    );
    this.setState({
      lists: lists,
      showSuccessAlert: true,
    });
  };

  createPopulatedList = (response: INewListItemsActionResponse) => {
    let lists = { ...this.state.lists };
    if (!lists[response.list.listType.name]) {
      lists[response.list.listType.name] = [];
    }
    lists[response.list.listType.name].push(
      MyListsService.convertResponseToList(response.list),
    );
    this.setState({
      lists: lists,
      showSuccessAlert: true,
      latestAddedList: response,
    });
  };

  hideAccountLists = () => {
    return (
      (!this.manageGroupLists && !this.manageRestrictions) &&
      this.props.itemsToAdd &&
      this.props.itemsToAdd.length > 0
    );
  };

  render() {
    return (
      <motion.div
        initial="closed"
        animate="open"
        exit="closed"
        variants={this.props.closeRight ? variantsReverse() : variants()}
        className={
          `my-lists ${this.props.className ? this.props.className : ""} ` +
          (this.props.closeRight ? "right-side" : "")
        }
        key="navmenu"
      >
        <div className="top-bar">
          <button
            className="close-button"
            onClick={() => {
              this.props.setOpen(false);
            }}
          >
            <ArrowIcon
              useCloseIcon={this.props.isSecondary !== true}
            ></ArrowIcon>
          </button>
          <span>Lists</span>
        </div>
        <div className="container">
          <div className="row form-group">
            <div className="col-6 add-a-list">
              {this.props.addToListEnabled && (
                <>
                  <AddList
                    createList={this.createList}
                    createPopulatedList={this.createPopulatedList}
                    itemsToAdd={this.props.itemsToAdd}
                    actionType={this.props.actionType}
                    manageGroupLists={this.manageGroupLists}
                    authorized={this.isAuthorized()}
                    loginHref={this.state.loginHref}
                  />
                  {this.state.showSuccessAlert && (
                    <SuccessAlert
                      attentionText={successAlertMsg.attentionText}
                      text={successAlertMsg.text}
                      timerLengthInMS={3500}
                      danger={false}
                      dangerText={"Error occurred!"}
                      dangerAttentionText={"Please try again."}
                      onClickClose={() =>
                        this.setState({ showSuccessAlert: false })
                      }
                    />
                  )}
                </>
              )}
            </div>
            <div className="col-6 manage-lists">
              <div className="text-right">
                <a href={this.state.manageListsHref}>Manage Lists</a>
              </div>
            </div>
          </div>

          <div className="row">
            {this.hideAccountLists() ? (
              <div
                key={ListTypes[0].key}
                className={`col-12 list-type ${ListTypes[0].listClass}`}
              >
                <div>{ListTypes[0].key} Lists</div>
                <div
                  className={`list-underline ${ListTypes[0].underlineClasses}`}
                ></div>
              </div>
            ) : (
              ListTypes.map((listType) => {
                return (
                  <button
                    key={listType.key}
                    onClick={() => {
                      this.selectListType(listType.key);
                    }}
                    className={`col-6 list-type ${listType.listClass} ${
                      this.state.selectedListType === listType.key
                        ? "selected"
                        : ""
                    }`}
                  >
                    <div>{listType.key} Lists</div>
                    <div
                      className={`list-underline ${listType.underlineClasses}`}
                    ></div>
                    <div
                      className={`list-selected ${listType.underlineClasses}`}
                    ></div>
                  </button>
                );
              })
            )}
          </div>

          {!this.isAuthorized() && (
            <div className="container ml-sign-in mt-3">
              <b className="mb-2 text-center d-block">
                Quick Access To All Your Lists
              </b>
              <p className="mb-4 text-center">
                Sign in or register to start adding your favorite items,
                services, and ideas to a list that fits your needs.
              </p>

              {this.state.loginHref && (
                <div className="row">
                  <div className="col-10 offset-1">
                    <a
                      className="btn btn-lg btn-primary font-weight-bold btn-block pl-5 pr-5"
                      href={this.state.loginHref}
                      role="button"
                      type="button"
                    >
                      SIGN IN OR REGISTER
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {this.isAuthorized() && !this.state.listsLoaded && (
            <div className="spinner-wrapper">
              <span
                className="spinner spinner-border"
                role="status"
                aria-hidden="true"
              />
            </div>
          )}

          {this.state.listsLoaded &&
            this.state.lists[this.state.selectedListType] &&
            this.state.lists[this.state.selectedListType].length !== 0 &&
            this.state.lists[this.state.selectedListType].map((list) => {
              let initialValue = null;
              if (list.id === this.state.latestAddedList?.list.id) {
                initialValue = this.state.latestAddedList;
              }
              return (
                <div className="row form-group" key={list.id}>
                  <div className="col-12">
                    <List
                      list={list}
                      selectList={this.selectList}
                      myListsHref={this.state.manageListsHref}
                      myListDetailsHref={this.state.myListDetailsHref}
                      useDefaultFavoritesList={
                        this.state.isEcom
                          ? this.state.useDefaultFavoritesList
                          : false
                      }
                      itemsToAdd={this.props.itemsToAdd}
                      actionType={this.props.actionType}
                      initialValue={initialValue}
                      manageGroupLists={this.manageGroupLists}
                      manageRestrictions={this.manageRestrictions}
                    ></List>
                  </div>
                </div>
              );
            })}
          {this.state.lists[this.state.selectedListType] &&
            this.state.lists[this.state.selectedListType].length === 0 && (
              <>
                <div className="text-center">No Lists</div>
              </>
            )}
        </div>
      </motion.div>
    );
  }
}

export default MyLists;
